import unorm from 'unorm'
import {remove, isEqual} from "lodash";

const dev = process.env.NODE_ENV === 'development'
const folder = dev
  ? 'http://localhost:3000/images'
  : 'http://cms.diferpan.com.br:3000/images'

export const getImage = (name) => `${folder}/${name}`

export const convertToBase64 = (selectedFile, cb) => {
  if (selectedFile.length > 0) {
    let fileToLoad = selectedFile[0]
    let fileReader = new FileReader()
    let base64
    fileReader.onload = function(fileLoadedEvent) {
      base64 = fileLoadedEvent.target.result
      cb(base64)
    }
    fileReader.readAsDataURL(fileToLoad)
  }
}

export const EMAIL_INVALID_MESSAGE =
  'Informe um e-mail válido (ex.: nome@dominio.com)'

const EMAIL_REGEX = /^[\p{L}\p{N}._%+-]+@[\p{L}\p{N}.-]+\.[\p{L}]{2,}$/u

export function isEmail(value = '') {
  return EMAIL_REGEX.test(value ?? '')
}

export function isCpf(y = '') {
  if (!y) return false
  var x = y.replace(/\D/g, '')
  var soma = 0
  var resto
  var i = 0
  if (x === '00000000000') {
    return false
  }
  for (i = 1; i <= 9; i += 1) {
    soma = soma + parseInt(x.substring(i - 1, i), 10) * (11 - i)
  }
  resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) {
    resto = 0
  }
  if (resto !== parseInt(x.substring(9, 10), 10)) {
    return false
  }
  soma = 0
  for (i = 1; i <= 10; i++) {
    soma = soma + parseInt(x.substring(i - 1, i), 10) * (12 - i)
  }
  resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) {
    resto = 0
  }
  if (resto !== parseInt(x.substring(10, 11), 10)) {
    return false
  }
  return true
}

// Sanitizador de CPF/CNPJ: remove apenas a pontuação da máscara, preservando
// letras — o CNPJ alfanumérico da Receita Federal (IN RFB 2229/2024) usa
// letras A-Z nas 12 primeiras posições; um strip de dígitos as apagaria.
export function unmaskDocumento(value = '') {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
}

// Valor de cada caractere no cálculo do DV do CNPJ alfanumérico (IN RFB
// 2229/2024): dígitos valem o próprio valor e letras valem ASCII - 48
// ('A' = 17 ... 'Z' = 42). Para CNPJ numérico o resultado é igual ao legado.
function cnpjCalculaDv(base, pesoInicial) {
  var soma = 0
  var peso = pesoInicial
  for (var i = 0; i < base.length; i++) {
    soma += (base.charCodeAt(i) - 48) * peso
    peso = peso === 2 ? 9 : peso - 1
  }
  var resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

export function isCnpj(value = '') {
  if (!value) return false
  var cnpj = unmaskDocumento(value)

  // 12 primeiras posições alfanuméricas + 2 dígitos verificadores numéricos
  if (!/^[0-9A-Z]{12}\d{2}$/.test(cnpj)) return false
  if (/^(.)\1{13}$/.test(cnpj)) return false

  var dv1 = cnpjCalculaDv(cnpj.substring(0, 12), 5)
  if (cnpj[12] !== String(dv1)) return false

  var dv2 = cnpjCalculaDv(cnpj.substring(0, 13), 6)
  return cnpj[13] === String(dv2)
}

export function normalizeString(value) {
  return unorm
    .nfd(value.toString())
    .trim()
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s\s+/g, ' ')
}

export function isNumeric(n) {
  return !isNaN(parseFloat(n)) && isFinite(n)
}

export function toMonetary(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

export function toDecimal(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'decimal',
  }).format(value)
}

export function toPercent(value, decimais = 2) {
  let percentVal = value / 100
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais
  }).format(percentVal)
}

export function toDateTime(date) {
  const options = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  }
  return new Intl.DateTimeFormat('pt-BR', options).format(new Date(date))
}

export function toDate(date) {
  const options = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  }
  return new Intl.DateTimeFormat('en-US', options).toFormat
}

export function formatDate(dateString) {
  if (!dateString) return "";

  const isoString = dateString.replace(" ", "T");

  const date = new Date(isoString);

  if (isNaN(date)) return dateString;

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// Domínios de e-mail tratados como usuário interno Versotech (equipe/parceiros).
// Usuários internos preenchem motivo guiado (ticket/implantação); clientes
// preenchem um motivo livre.
export const DOMINIOS_INTERNOS = ['versotech.com.br', 'bdti.com.br'];

// Colaborador logado, lido da carga em localStorage ('app').
export function getColaboradorLogado() {
  try {
    const { colaborador } = JSON.parse(localStorage.getItem('app')) || {};
    return colaborador || null;
  } catch (e) {
    return null;
  }
}

// True quando o e-mail pertence a um domínio interno da Versotech.
export function isEmailInterno(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }
  const dominio = email.trim().toLowerCase().split('@')[1] || '';
  return DOMINIOS_INTERNOS.includes(dominio);
}

export function loadDescs(moduloName) {
  const { modulos } = JSON.parse(localStorage.getItem('app'))
  const { descPlural, descSingular } = modulos.find(
    (m) => m.nome === moduloName
  )
  return {
    descPlural,
    descSingular
  }
}

export function intToYesOrNo(intValue = -1) {
  if (intValue === 1) {
    return 'Sim'
  }

  if (intValue === 0) {
    return 'Não'
  }

  return 'N/A'
}

export function makeHash(size = 5) {
  let text = "";
  let possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  for (let i = 0; i < size; i++)
    text += possible.charAt(Math.floor(Math.random() * possible.length));

  return text
}

export function baseName(url) {
  return url.split('/').pop();
}

export function toggleValue(values, value) {
    const aux = values.slice(0)
    const removed = remove(aux, (n) => n === value)
    if (!removed.length) aux.push(value)
    return aux
}

export function toggleObjByIndex(values, value, indexBy) {
    const aux = values.slice(0)
    const removed = remove(aux, (n) => n[indexBy] === value[indexBy])
    if (!removed.length) aux.push(value)
    return aux
}

/**
 * Filtra um array de objetos comparando várias chaves
 * @param array
 * @param termo
 * @param fieldsList
 * @return {*}
 */
export function arrayFilter(array, termo, fieldsList) {
  if (termo === '') {
    return array;
  }

  return array.filter((arrayItem) => {
    return stringContainsObject(termo, fieldsList, arrayItem);
  });
}

/**
 * Compara um termo entre várias chaves do array
 * @param termo
 * @param fieldsList
 * @param objectItem
 * @return {boolean}
 */
export function stringContainsObject(termo, fieldsList, objectItem) {

  if (termo === '') {
    return true;
  }

  let stringComparator = '';

  fieldsList.map((field) => {
    stringComparator += ' ' + normalizeString(`${objectItem[field]}`.toLowerCase());
  });

  const stringsToCompare = normalizeString(`${termo}`.toLowerCase()).split(' ');

  for (let i = 0; i < stringsToCompare.length ; i++) {
    if (stringComparator.indexOf(stringsToCompare[i]) < 0) {
      return false
    }
  }

  return true;
}

export function removeDuplicados(array, key = "id") {
  return array.filter(
    (item, index, self) =>
      index == self.findIndex((obj) => obj[key] == item[key])
  );
}

export const FLAGS_HIERARQUIA = ["COMPRADOR", "GESTOR_NIVEL_2"];

export function isSubCliente(perfis, idPerfil) {
  const perfil = perfis.find((perfil) => perfil.id === idPerfil);

  return !!perfil && FLAGS_HIERARQUIA.includes(perfil.flags);
}

/**
 * Igualdade profunda insensível à ordem das chaves (delega ao lodash.isEqual).
 * Diferente de comparar JSON.stringify: { a, b } === { b, a }, e tipos
 * primitivos coeridos (1 vs '1', null vs undefined) NÃO são iguais.
 */
export function deepEqual(a, b) {
  return isEqual(a, b);
}

export function get_value(obj, path, defaultValue = null) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj) ?? defaultValue;
}

export function validateConteudo(value) {
  const textoSemHtml = value ? value.replace(/<[^>]*>/g, "").trim() : "";
  if (!textoSemHtml) {
    return `O campo conteúdo não pode estar vazio. Caso a edição de código esteja ativa, desative antes de salvar`;
  }
}